import { createWalletClient, custom, type Address } from "viem";
import type { ChainConfig } from "./chains";
import { erc20Abi } from "./cctp";
import type { Eip1193Provider } from "./wallet";
import { readApiJson } from "./operations";

export type TestnetGasQuote = {
  enabled: boolean;
  treasuryAddress: Address | null;
  saleUsd: string;
  paymentUsdc: string;
  paymentUsdcRaw: string;
  nativeSymbol: "ETH";
  nativeAmount: string;
  nativeAmountWei: string;
  referenceEthUsd: string;
  markupBps: number;
};

export type TestnetGasDelivery = {
  ok: true;
  txHash: string;
  nativeAmount: string | null;
  nativeSymbol: string;
  alreadyDelivered?: boolean;
};

export async function fetchTestnetGasQuote(destinationDomain: number, gasTopupUsd: string) {
  const response = await fetch("/api/gas/quote", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ destinationDomain, gasTopupUsd })
  });
  return readApiJson<TestnetGasQuote>(response, "Destination gas quote unavailable.");
}

export async function switchOrAddEvmChain(provider: Eip1193Provider, chain: ChainConfig) {
  try {
    await provider.request({ method: "wallet_switchEthereumChain", params: [{ chainId: `0x${chain.viem.id.toString(16)}` }] });
  } catch (error) {
    const candidate = error as { code?: number };
    if (candidate.code !== 4902) throw error;
    await provider.request({
      method: "wallet_addEthereumChain",
      params: [{
        chainId: `0x${chain.viem.id.toString(16)}`,
        chainName: chain.viem.name,
        nativeCurrency: chain.viem.nativeCurrency,
        rpcUrls: [...chain.viem.rpcUrls.default.http],
        blockExplorerUrls: chain.viem.blockExplorers?.default?.url ? [chain.viem.blockExplorers.default.url] : []
      }]
    });
  }
}

export async function payForAndDeliverTestnetGas(args: {
  provider: Eip1193Provider;
  account: Address;
  source: ChainConfig;
  destinationDomain: number;
  operationId: string;
  gasTopupUsd: string;
  onStep?: (message: string) => void;
}) {
  const quote = await fetchTestnetGasQuote(args.destinationDomain, args.gasTopupUsd);
  if (!quote.treasuryAddress) throw new Error("FreshMint test gas treasury is not configured.");

  args.onStep?.(`Pay ${quote.paymentUsdc} test USDC for destination gas`);
  await switchOrAddEvmChain(args.provider, args.source);
  const walletClient = createWalletClient({ chain: args.source.viem, transport: custom(args.provider), account: args.account });
  const paymentHash = await walletClient.writeContract({
    address: args.source.usdc,
    abi: erc20Abi,
    functionName: "transfer",
    args: [quote.treasuryAddress, BigInt(quote.paymentUsdcRaw)],
    account: args.account,
    chain: args.source.viem
  });

  // Hand the hash to the backend immediately. The backend persists it before waiting for
  // confirmation, so closing the tab or an RPC indexing delay cannot orphan a payment.
  args.onStep?.("Saving gas-payment transaction · waiting for confirmation");
  const response = await fetch(`/api/operations/${encodeURIComponent(args.operationId)}/gas/deliver`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ paymentTxHash: paymentHash })
  });
  const delivery = await readApiJson<TestnetGasDelivery>(response, "Destination gas delivery failed.");
  args.onStep?.(`Destination gas delivered · ${delivery.nativeAmount ?? quote.nativeAmount} ${delivery.nativeSymbol}`);
  return { quote, paymentHash, delivery };
}
